import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

export function RegisterPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, signUpWithEmail, signInWithGoogle, refreshUser } = useAuth()
  
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [invitationInfo, setInvitationInfo] = useState<any>(null)
  
  const invitationToken = searchParams.get('invitation')

  const handleAcceptInvitation = async () => {
    if (!invitationToken || !user) return

    try {
      // Firebase Authからトークンを取得
      const auth = (await import('firebase/auth')).getAuth()
      const currentUser = auth.currentUser
      if (!currentUser) {
        throw new Error('ユーザーが認証されていません')
      }

      const token = await currentUser.getIdToken()
      const response = await fetch(`${API_BASE}/v1/invitations/${invitationToken}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || '招待の受諾に失敗しました')
      }

      // Custom Claimsが反映されるまで待機
      console.log('✅ 招待を受諾しました。Custom Claimsの反映を待機中...')
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // トークンを強制リフレッシュしてCustom Claimsを取得
      await currentUser.getIdToken(true)
      console.log('✅ トークンをリフレッシュしました')
      
      // AuthContextの状態が更新されるまで待機（最大10秒）
      let retries = 0
      const maxRetries = 20 // 0.5秒 × 20 = 10秒
      while (retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // トークンを再取得してCustom Claimsを確認
        const tokenResult = await currentUser.getIdTokenResult()
        const claims = tokenResult.claims as any
        
        if (claims.orgId) {
          console.log('✅ Custom Claimsが反映されました:', { orgId: claims.orgId, role: claims.role })
          break
        }
        
        retries++
        console.log(`⏳ Custom Claimsの反映待機中... (${retries}/${maxRetries})`)
      }

      // AuthContextの状態を強制的に更新
      console.log('🔄 AuthContextの状態を更新中...')
      await refreshUser()
      
      // 少し待機してから遷移
      await new Promise(resolve => setTimeout(resolve, 500))

      // 成功したらホームにリダイレクト
      console.log('✅ ホームページにリダイレクトします')
      navigate('/')
    } catch (error) {
      console.error('Error accepting invitation:', error)
      setError(error instanceof Error ? error.message : '招待の受諾に失敗しました')
    }
  }

  // 招待情報を取得
  useEffect(() => {
    const fetchInvitationInfo = async () => {
      if (!invitationToken) return

      try {
        const response = await fetch(`${API_BASE}/v1/invitations/${invitationToken}/info`)
        if (response.ok) {
          const data = await response.json()
          setInvitationInfo({
            organizationName: data.organizationName,
            invitedBy: data.invitedByName || data.invitedByEmail
          })
        } else {
          // APIが存在しない場合は仮のデータを使用
          setInvitationInfo({
            organizationName: 'デモ組織',
            invitedBy: 'admin@example.com'
          })
        }
      } catch (error) {
        console.error('Failed to fetch invitation info:', error)
        // エラーの場合も仮のデータを使用
        setInvitationInfo({
          organizationName: 'デモ組織',
          invitedBy: 'admin@example.com'
        })
      }
    }

    fetchInvitationInfo()
  }, [invitationToken])

  // 既にログイン済みの場合
  useEffect(() => {
    if (user) {
      // 招待トークンがある場合は受諾処理
      if (invitationToken) {
        handleAcceptInvitation()
      } else if (user.orgId) {
        // 既に組織に所属している場合はホームへ
        navigate('/')
      } else {
        // 組織に所属していない場合は組織作成ページへ
        navigate('/create-organization')
      }
    }
  }, [user, invitationToken])

  const handleGoogleSignIn = async () => {
    setError('')
    setLoading(true)

    try {
      await signInWithGoogle()
      
      // 招待トークンがある場合は受諾処理
      if (invitationToken) {
        await handleAcceptInvitation()
      } else {
        // 招待なしの場合は組織作成ページへ
        navigate('/create-organization')
      }
    } catch (error: any) {
      console.error('Google sign-in error:', error)
      setError(error.message || 'Googleログインに失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // バリデーション
    if (!email || !password || !confirmPassword) {
      setError('すべての項目を入力してください')
      return
    }

    if (password !== confirmPassword) {
      setError('パスワードが一致しません')
      return
    }

    if (password.length < 6) {
      setError('パスワードは6文字以上で入力してください')
      return
    }

    setLoading(true)

    try {
      // ユーザー登録
      await signUpWithEmail(email, password)

      // 招待トークンがある場合は受諾処理
      if (invitationToken) {
        await handleAcceptInvitation()
      } else {
        // 招待なしの場合は組織作成ページへ
        navigate('/create-organization')
      }
    } catch (error: any) {
      console.error('Registration error:', error)
      setError(error.message || '登録に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex-1 w-full overflow-y-auto flex items-center justify-center" style={{ background: 'var(--color-surface)' }}>
      <div className="w-full px-6 py-12" style={{ maxWidth: 420 }}>
        <div className="card w-full">
        {/* ヘッダー */}
        <div className="text-center" style={{ marginBottom: 'var(--space-6)' }}>
          <span className="brand-logo text-2xl">BluePaper</span>
          <p className="text-sm text-secondary" style={{ marginTop: 'var(--space-2)' }}>
            アカウント登録
          </p>
          {invitationInfo && (
            <div className="alert info text-left" style={{ marginTop: 'var(--space-4)' }}>
              <div className="alert-body">
                <p className="alert-title text-sm">
                  「{invitationInfo.organizationName}」への招待
                </p>
                <p className="text-xs">
                  招待者: {invitationInfo.invitedBy}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 登録フォーム */}
        <div className="space-y-6">
          {error && (
            <div className="alert danger">
              <div className="alert-body">
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Google SSO ボタン */}
          <div>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="btn btn-outline btn-lg w-full"
            >
              <svg style={{ width: 20, height: 20 }} viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {loading ? '処理中...' : 'Googleで続ける'}
            </button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full" style={{ borderTop: '1px solid var(--color-border-light)' }}></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 text-tertiary" style={{ background: 'var(--color-base)' }}>または</span>
            </div>
          </div>

        <form onSubmit={handleSubmit}>

          <div>
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                メールアドレス
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-input"
                placeholder="user@example.com"
                disabled={loading}
              />
              {invitationToken && (
                <p className="form-hint">
                  ※招待されたメールアドレスを使用してください
                </p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                パスワード
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                placeholder="6文字以上"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirm-password" className="form-label">
                パスワード（確認）
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="form-input"
                placeholder="パスワードを再入力"
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary btn-lg w-full"
            >
              {loading ? (
                <>
                  <span className="spinner sm" style={{ borderTopColor: 'var(--color-text-inverse)' }} />
                  登録中...
                </>
              ) : invitationToken ? (
                '登録して組織に参加'
              ) : (
                '登録'
              )}
            </button>
          </div>

          <div className="text-center" style={{ marginTop: 'var(--space-5)' }}>
            <p className="text-sm text-secondary">
              既にアカウントをお持ちですか？{' '}
              <a href="/login" className="font-medium text-accent">
                ログイン
              </a>
            </p>
          </div>
        </form>
        </div>
        </div>
      </div>
    </div>
  )
}
